import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Col, Tooltip, Result, Empty } from '@bedrock/components';
import Error from '@bedrock/components/es/Empty/images/small/Error';
import { ColProps } from '@bedrock/components/lib/Col';
import { HelpCircle } from '@bedrock/icons-react';
import { ErrorBoundary } from 'react-error-boundary';
import SFormElement, { SFormComponentClass } from '../SFormElement';
import { SlotComponent } from '../SFormElementWrapper';
import cx from 'classnames';
import './index.less';
import {
  formItemKeyToList,
  DefaultRequiredRuleMap,
} from '#components/SForm/SFormItem/utils';
import FormItemValueTransform from '#components/SForm/SFormItem/FormItemValueTransform';
import { ScoutCaptureError } from '#utils/scout';
import _ from 'lodash';
import { TooltipProps } from '@bedrock/components/es/Tooltip';
import { useMemoizedFn } from 'ahooks';
import {
  isTestBridgeEnabled,
  buildTestId,
  getFormIdByInstance,
} from '../../../test-bridge';

export const getLabelTip = (label, labelTip) => {
  let toolTipProps = {};
  if (_.isObject(labelTip) && !React.isValidElement(labelTip)) {
    toolTipProps = labelTip;
  }
  return (
    <>
      <span style={{ marginRight: '4px' }}>{label}</span>
      <span className="label-icon">
        <Tooltip title={labelTip} placement="top" {...toolTipProps}>
          <HelpCircle />
        </Tooltip>
      </span>
    </>
  );
};

// 函数生成T或者T本身
type AbleFunc<T, params = any> = T | ((params: params) => T);

type AbleFuncObj<T> = {
  [P in keyof T]: AbleFunc<T[P], any>;
};

export type FormItemKeyType = (string | number)[] | string | number;

/** 校验错误展示方式 —— 详见 `SFormItemProps.errorShowType` */
export type SFormErrorShowType = 'bottom' | 'head' | 'popover';

/**
 * 非 `'bottom'` 模式的样式类（Tailwind 任意变体，**必须是完整字面量**才能被 JIT 扫到）。
 *
 * 【为什么不用新增/修改 `.less`】项目 Tailwind-first 契约禁止动 Less；
 * 而任意变体只作用于**自身子树**，天然比全局 less 选择器更不易误伤其他组件。
 *
 * 【为什么这些属性】bedrock 的原生错误节点是 `.rock-form-item-input-explain`（在控件下方、占位）：
 * - `absolute` + `bottom-full` + `m-0` → 脱离文档流并浮到控件上方，**不再占位**；
 * - `opacity-0` + `pointer-events-none` → 平时不可见不可点；
 * - `[&:hover_…]:opacity-100` → 鼠标移到该表单项上才出现（需求方明确要求）；
 * - 深色气泡配色对齐 bedrock Tooltip 的观感。
 *
 * ⚠️ **刻意不加 `relative`**：本项目 Tailwind 开了全局 `important`，`.relative` 与撑满层用的
 * `.\!absolute` 会**同为 `!important`**，同权重下由样式表生成顺序决定胜负（实测 `relative` 胜出，
 * 撑满静默失效）。`absolute` 元素本身就是定位祖先，因此**撑满场景无需 relative**；
 * 其余场景由调用方提供 positioned 宿主（评分表统一给 `td` 加 `relative`）。
 */
const ERROR_SHOW_TYPE_CLASS: Record<Exclude<SFormErrorShowType, 'bottom'>, string> =
  {
    head:
      '[&_.rock-form-item-input-explain]:!absolute ' +
      '[&_.rock-form-item-input-explain]:!bottom-[calc(100%+6px)] ' +
      '[&_.rock-form-item-input-explain]:!left-0 ' +
      '[&_.rock-form-item-input-explain]:!z-20 ' +
      '[&_.rock-form-item-input-explain]:!m-0 ' +
      '[&_.rock-form-item-input-explain]:!whitespace-nowrap ' +
      '[&_.rock-form-item-input-explain]:!rounded ' +
      '[&_.rock-form-item-input-explain]:!bg-[#1E1D29] ' +
      '[&_.rock-form-item-input-explain]:!px-2 ' +
      '[&_.rock-form-item-input-explain]:!py-1 ' +
      '[&_.rock-form-item-input-explain]:!text-sm ' +
      '[&_.rock-form-item-input-explain]:!text-white ' +
      '[&_.rock-form-item-input-explain]:!opacity-0 ' +
      '[&_.rock-form-item-input-explain]:!pointer-events-none ' +
      '[&_.rock-form-item-input-explain]:!transition-opacity ' +
      '[&:hover_.rock-form-item-input-explain]:!opacity-100',
    popover:
      '[&_.rock-form-item-input-explain]:!absolute ' +
      '[&_.rock-form-item-input-explain]:!bottom-[calc(100%+6px)] ' +
      '[&_.rock-form-item-input-explain]:!left-0 ' +
      '[&_.rock-form-item-input-explain]:!z-30 ' +
      '[&_.rock-form-item-input-explain]:!m-0 ' +
      '[&_.rock-form-item-input-explain]:!whitespace-nowrap ' +
      '[&_.rock-form-item-input-explain]:!rounded ' +
      '[&_.rock-form-item-input-explain]:!bg-[#1E1D29] ' +
      '[&_.rock-form-item-input-explain]:!px-2 ' +
      '[&_.rock-form-item-input-explain]:!py-1 ' +
      '[&_.rock-form-item-input-explain]:!text-sm ' +
      '[&_.rock-form-item-input-explain]:!text-white ' +
      '[&_.rock-form-item-input-explain]:!shadow-[0_2px_8px_rgba(0,0,0,0.16)] ' +
      '[&_.rock-form-item-input-explain]:!opacity-0 ' +
      '[&_.rock-form-item-input-explain]:!pointer-events-none ' +
      '[&_.rock-form-item-input-explain]:!transition-opacity ' +
      '[&:hover_.rock-form-item-input-explain]:!opacity-100',
  };

type SFormItemParentHighlightColor = 'light-orange';

export type SFormItemPropsData<
  T extends SFormComponentClass = SFormComponentClass,
> = {
  formItemKeyMap?: Record<string, FormItemKeyType>;
  formItem?: boolean;
  formItemKey: FormItemKeyType;
  renderData?: any;
  label?: string | number;
  labelTip?: string | Partial<TooltipProps> | React.ReactElement;
  labelText?: string;
  required?: boolean;
  componentClass?: T;
  className?: string;
  view?: boolean;
  show?: boolean | 'disableNone';
  processType?: string;
  col?: number | ColProps; // 布局
  offsetAfter?: number; // 偏移
  elementProps?: any; // 使用 any 避免类型约束问题
  valueFormat?: {
    /**
     * 外部value转为内部fieldValue
     * @param value 外部传入的value
     * @param fieldProps 字段属性
     * @returns 内部控件可用的fieldValue
     */
    valueToField?: (value: any, fieldProps: any) => any;
    /**
     * 内部fieldValue转为外部value
     * @param fieldValue 控件内部的值
     * @param fieldProps 字段属性
     * @returns 外部表单需要的value
     */
    fieldToValue?: (fieldValue: any, fieldProps: any) => any;
  };
  hideError?: boolean; // 是否隐藏错误提示
  /**
   * 校验错误的**展示方式**（默认 `'bottom'` —— 与既有行为逐像素一致）。
   *
   * - `'bottom'`：bedrock 原生行为，错误文案渲染在控件**下方并占位**
   * - `'head'`：错误文案浮在控件上方（不占位）
   * - `'popover'`：错误文案变成**悬停气泡** —— 平时不出现、鼠标移上去才出现，且不占位
   *
   * ⚠️ 实现方式是「附加 class + Tailwind 任意变体」，**不改动 DOM 结构**。
   * 这一点是刻意的：合并单元格里的输入框靠 `td.relative` → `absolute` 层 → 逐层 `height:100%`
   * 撑满，一旦插入包裹层就会把这条高度链截断（控件塌回默认高度）。纯样式方案没有这个副作用。
   *
   * ⚠️ 之所以走纯样式而非自绘 Tooltip：bedrock 的 `FormItemInput` 把 `errors` 与
   * `isErrorVisible` 都收在内部（`isErrorVisible` 是内部 state，`errors` 不在 `Form.Item` 公开 props 里），
   * **没有 prop 可以关掉原生渲染**；而错误文案本身已在 DOM 中（`.rock-form-item-input-explain`），
   * 因此「挪位置 + 控制显隐」比「取错误值再自绘」既更简单也更不易与既有渲染冲突。
   */
  errorShowType?: SFormErrorShowType;
  render?: (
    renderData: any,
    itemProps: SFormItemPropsData
  ) => React.ReactElement;
  labelWrapper?: (props: { label: any }, renderData: any) => React.ReactElement;
  watchChildFormValidate?: boolean | 'string'; // 是否监听子元素Form的校验，子元素的ref如果有validateFields方法，会被添加进校验队列
  // 前置插槽
  renderBeforeSlot?: (
    renderData: any,
    itemProps: SFormItemPropsData
  ) => React.ReactElement;
  // 后置插槽
  renderAfterSlot?: (
    renderData: any,
    itemProps: SFormItemPropsData
  ) => React.ReactElement;
  // 父元素背景高亮颜色
  parentHighlightColor?:
    | SFormItemParentHighlightColor
    | ((
        renderData: any,
        itemProps: SFormItemPropsData
      ) => SFormItemParentHighlightColor | undefined | null);
} & Omit<
  React.ComponentProps<typeof Form.Item>,
  'label' | 'required' | 'name' | 'className'
>;

export type SFormItemProps<
  T extends FormItemKeyType = FormItemKeyType,
  U extends SFormComponentClass = SFormComponentClass,
> = {
  formItemKey: T;
} & AbleFuncObj<Omit<SFormItemPropsData<U>, 'formItemKey'>>;

// 函数参数，不再转化
const FUNCTION_CONFIG = [
  'render',
  'labelWrapper',
  'renderBeforeSlot',
  'renderAfterSlot',
  'parentHighlightColor',
];

// AbleFuncObj 转化为 SFormItemPropsData
export const configFunctionToValue = (
  props: SFormItemProps,
  renderData: any
): SFormItemPropsData => {
  // 如果是函数，就执行
  const { ...rets } = props;
  const renderProps = Object.keys(rets).reduce((prev, key) => {
    const value = props[key];
    if (FUNCTION_CONFIG.includes(key)) {
      prev[key] = value;
      return prev;
    }
    prev[key] = typeof value === 'function' ? value(renderData) : value;
    return prev;
  }, {});
  return renderProps as SFormItemPropsData;
};

export default function SFormItem<
  T extends FormItemKeyType = FormItemKeyType,
  U extends SFormComponentClass = SFormComponentClass,
>(props: SFormItemProps<T, U>): React.ReactElement {
  const renderData = props.renderData || {};

  const renderProps = useMemo(() => {
    return configFunctionToValue(props, renderData);
  }, [props]);

  // A12: testId 注入准备 — 查 WeakMap formId（覆盖 useSForm + 直接 SFormItem 场景，C2 修订）
  const formInstance = Form.useFormInstance();
  const bridgeFormId = getFormIdByInstance(formInstance);

  const {
    formItemKeyMap = null,
    formItem = true,
    label,
    labelTip,
    labelText,
    labelWrapper,
    required,
    formItemKey,
    componentClass,
    className,
    view,
    show = true,
    elementProps = {},
    rules,
    col,
    offsetAfter,
    dependencies,
    watchChildFormValidate,
    hideError = false,
    // 默认 'bottom'：不传时完全走 bedrock 原生路径，零行为变化
    errorShowType = 'bottom',
    valueFormat,
    renderBeforeSlot,
    renderAfterSlot,
    parentHighlightColor: parentHighlightColorFromProps,
    ...itemProps
  } = renderProps;

  const elementRef = useRef(null);

  // 创建包装器组件来处理 valueFormat
  const WrappedSFormElement = useMemo(() => {
    if (!valueFormat) {
      return SFormElement;
    }

    const Wrapper = React.forwardRef<any, any>((fieldProps, ref) => {
      const { value, onChange, ...restFieldProps } = fieldProps;
      const restFieldPropsRef = useRef(restFieldProps);
      restFieldPropsRef.current = restFieldProps;

      const [internalValue, setInternalValue] = useState<any>(() => {
        // 初始化时，如果有外部value，转换为内部值
        if (value !== undefined && valueFormat.valueToField) {
          return valueFormat.valueToField(value, restFieldPropsRef.current);
        }
        return value;
      });

      // 当外部value变化时，同步到内部
      useEffect(() => {
        if (value !== undefined && valueFormat.valueToField) {
          const newInternalValue = valueFormat.valueToField(
            value,
            restFieldPropsRef.current
          );
          setInternalValue(newInternalValue);
        } else {
          setInternalValue(value);
        }
      }, [value, valueFormat.valueToField]);

      const handleInternalChange = useMemoizedFn(
        (newInternalValue: any, ...args: any[]) => {
          // 转换为外部值并调用外部onChange
          if (onChange && valueFormat.fieldToValue) {
            const externalValue = valueFormat.fieldToValue(
              newInternalValue,
              restFieldPropsRef.current
            );
            onChange(externalValue, ...args);
          } else if (onChange) {
            onChange(newInternalValue, ...args);
          }
        }
      );

      return (
        <SFormElement
          {...restFieldProps}
          value={internalValue}
          onChange={handleInternalChange}
          ref={ref}
        />
      );
    });

    Wrapper.displayName = 'ValueFormatWrapper';
    return Wrapper;
  }, [valueFormat]);

  const ablerules: any = useMemo(() => {
    // show 不为 true 时不进行任何校验，rules 置为空
    const ruleList = [...(rules || [])];
    if (view) return [];
    if (required) {
      if (DefaultRequiredRuleMap[componentClass]) {
        ruleList?.unshift?.({
          label,
          message: `此项必填`,
          ...DefaultRequiredRuleMap[componentClass],
        });
      } else {
        ruleList?.unshift?.({ required: true, message: `此项必填` });
      }
    }

    // 非必填的上传组件，仍需校验上传中状态
    // if (!required && componentClass === 'upload') {
    //   ruleList.push({
    //     validator: (rule, value) => {
    //       if (value?.some((n) => n.status === 'uploading')) {
    //         return Promise.reject('上传中');
    //       }
    //       return Promise.resolve();
    //     }
    //   });
    // }

    if (watchChildFormValidate) {
      let validateFnName = 'validateFields';
      if (typeof watchChildFormValidate === 'string') {
        validateFnName = watchChildFormValidate;
      }
      ruleList.push({
        validator: (rule, value) => {
          if (!elementRef.current?.[validateFnName]) {
            return Promise.resolve();
          }
          // 兜底优化，如果方法返回的不是promise，则promise包裹
          return Promise.resolve(elementRef.current?.[validateFnName]?.());
        },
      });
    }

    return ruleList;
  }, [required, rules, view, watchChildFormValidate]);

  const showRequired = useMemo(() => {
    if (view) return false;
    if (required) {
      return true;
    }
    return ablerules.some((rule) => rule.required);
  }, [required, view, ablerules]);

  const colProps = useMemo(() => {
    if (typeof col === 'number') {
      return { span: col };
    }
    return col;
  }, [col]);

  // 创建插槽函数（统一用于 SlotComponent 和 WrappedSFormElement）
  // SlotComponent 现在支持函数形式，可以直接传递函数
  const beforeSlotFn = useMemo(() => {
    if (!renderBeforeSlot) return undefined;
    return (value: any, props: any) => {
      return renderBeforeSlot(renderData, renderProps);
    };
  }, [renderBeforeSlot, renderData, renderProps]);

  const afterSlotFn = useMemo(() => {
    if (!renderAfterSlot) return undefined;
    return (value: any, props: any) => {
      return renderAfterSlot(renderData, renderProps);
    };
  }, [renderAfterSlot, renderData, renderProps]);

  const parentHighlightColor = useMemo(() => {
    if (!parentHighlightColorFromProps) return null;
    if (typeof parentHighlightColorFromProps === 'function') {
      return parentHighlightColorFromProps(renderData, renderProps);
    }
    return parentHighlightColorFromProps;
  }, [parentHighlightColorFromProps, renderData, renderProps]);

  if (!show) return null;

  const isDisableNone = show === 'disableNone';

  // 创建 item，如果 watchChildFormValidate 为 true，则通过 forwardRef 传递 ref
  let item: any = props.render ? (
    <SlotComponent
      beforeSlot={beforeSlotFn}
      afterSlot={afterSlotFn}
      needValueAndOnChange={true}
      ref={watchChildFormValidate ? elementRef : undefined}
    >
      {
        props?.render?.(
          props?.renderData,
          renderProps
        ) as React.ReactElement | null
      }
    </SlotComponent>
  ) : (
    <WrappedSFormElement
      componentClass={componentClass}
      view={view}
      beforeSlot={beforeSlotFn}
      afterSlot={afterSlotFn}
      ref={watchChildFormValidate ? elementRef : undefined}
      {...elementProps}
    />
  );

  // 如果是formItem，就包裹一层Form.Item
  const formItemConfig: any = {
    label: labelTip ? getLabelTip(label, labelTip) : label,
    required: showRequired,
    name: formItemKeyToList(formItemKey),
    rules: ablerules,
    className: cx(
      's-form-item',
      {
        form_item_hidden_error: hideError,
        [`form-item-parent-highlight-${parentHighlightColor}`]:
          !!parentHighlightColor,
      },
      // 非 'bottom' 模式才附加错误展示样式类；'bottom' 时传 undefined，class 列表与改造前逐字符一致
      errorShowType !== 'bottom' && ERROR_SHOW_TYPE_CLASS[errorShowType],
      className
    ),
    dependencies: dependencies?.map((n) => formItemKeyToList(n)),
    ...itemProps,
  };

  // A12: testId 注入（domProps 确定路径，bedrock FormItem.js:105 根 div 展开 domProps）
  if (isTestBridgeEnabled() && bridgeFormId) {
    formItemConfig.domProps = {
      ...(formItemConfig.domProps || {}),
      'data-testid': buildTestId(bridgeFormId, formItemKey),
    };
  }

  if (isDisableNone) {
    formItemConfig.style = {
      ...(formItemConfig.style || {}),
      display: 'none',
    };
    formItemConfig.rules = [];
  }
  // console.log('formItemConfig依赖', formItemConfig)
  if (labelWrapper) {
    formItemConfig.label = labelWrapper(
      { label: formItemConfig.label },
      renderData
    );
  }

  if (formItemKeyMap) {
    item = (
      <FormItemValueTransform keyMap={formItemKeyMap} {...formItemConfig}>
        {item}
      </FormItemValueTransform>
    );
  } else if (formItem) {
    item = <Form.Item {...formItemConfig}>{item}</Form.Item>;
  }

  item = (
    <ErrorBoundary
      FallbackComponent={({ error }) => {
        ScoutCaptureError(error, 'SFormItemError');
        console.error('SformItem渲染错误', error);
        return (
          <Empty
            style={{
              width: '100%',
              minWidth: '400px',
            }}
            imageNode={<Error />}
            text="渲染错误"
            description={'SformItem渲染错误'}
          />
        );
      }}
    >
      {item}
    </ErrorBoundary>
  );

  // 如果有col，就包裹一层Col
  if (!!col) {
    return (
      <>
        <Col {...colProps}>{item as React.ReactElement}</Col>
        {offsetAfter && <Col span={offsetAfter} />}
      </>
    );
  }

  return item as React.ReactElement;
}
